import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Eye, UserPlus, Ban, Check, Eye as EyeIcon, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AgentsTab = ({ token }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [newAgent, setNewAgent] = useState({
    full_name: '',
    email: '',
    password: '',
    phone_number: '',
    role: 'agent',
  });

  // Cookie auth (Phase 0); omit Bearer when no legacy token to avoid "Bearer null".
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/agents`, { headers });
      setAgents(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async () => {
    if (!newAgent.full_name || !newAgent.email || !newAgent.password || !newAgent.phone_number) {
      toast.error('All fields are required');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/agents`, newAgent, { headers });
      toast.success('Agent account created');
      setCreateOpen(false);
      setNewAgent({ full_name: '', email: '', password: '', phone_number: '', role: 'agent' });
      setShowPassword(false);
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    } finally {
      setCreating(false);
    }
  };

  const handleViewDetails = (agent) => {
    setCurrentAgent(agent);
    setDetailOpen(true);
  };

  const handleStatusChange = async (agentId, newStatus) => {
    try {
      await axios.put(`${API}/admin/agents/${agentId}/status`, { status: newStatus }, { headers });
      toast.success(`Agent ${newStatus}`);
      fetchAgents();
      setDetailOpen(false);
    } catch (error) {
      toast.error('Failed to update agent status');
    }
  };

  const handleCancelCreate = () => {
    setCreateOpen(false);
    setNewAgent({ full_name: '', email: '', password: '', phone_number: '', role: 'agent' });
    setShowPassword(false);
  };

  return (
    <div className="space-y-6">
      {/* Header card with Create button */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>Call Center Agents</CardTitle>
            <CardDescription>Manage agent accounts for your call center team</CardDescription>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
          >
            <UserPlus size={16} className="mr-2" /> Create Agent
          </Button>
        </CardHeader>
      </Card>

      {/* Agents Table */}
      <Card>
        <CardHeader>
          <CardTitle>Agents</CardTitle>
          <CardDescription>Total: {agents.length} agent{agents.length !== 1 ? 's' : ''}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="animate-spin" size={24} />
            </div>
          ) : agents.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <UserPlus size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">No agents yet</p>
              <p className="text-sm mt-1">Click "Create Agent" to add your first call center agent</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader className="bg-[#F5EDE4]">
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agents.map((agent) => (
                    <TableRow key={agent.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">{agent.full_name}</TableCell>
                      <TableCell className="text-sm">{agent.email}</TableCell>
                      <TableCell className="text-sm">{agent.phone_number}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">
                          {agent.role || 'agent'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={agent.status === 'active' ? 'default' : 'destructive'}>
                          {agent.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {agent.last_login
                          ? new Date(agent.last_login).toLocaleDateString()
                          : 'Never'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetails(agent)}
                        >
                          <Eye size={14} className="mr-1" /> View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Agent Sheet */}
      <Sheet open={createOpen} onOpenChange={handleCancelCreate}>
        <SheetContent className="w-[420px]">
          <SheetHeader>
            <SheetTitle>Create Agent Account</SheetTitle>
            <SheetDescription>Set up a new call center agent login</SheetDescription>
          </SheetHeader>

          <div className="space-y-5 mt-6">
            <div className="space-y-2">
              <Label htmlFor="agent-name">Full Name</Label>
              <Input
                id="agent-name"
                placeholder="e.g. Jane Doe"
                value={newAgent.full_name}
                onChange={(e) => setNewAgent({ ...newAgent, full_name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="agent-email">Email</Label>
              <Input
                id="agent-email"
                type="email"
                placeholder="agent@callcenter.co.za"
                value={newAgent.email}
                onChange={(e) => setNewAgent({ ...newAgent, email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="agent-phone">Phone Number</Label>
              <Input
                id="agent-phone"
                placeholder="+27829999999"
                value={newAgent.phone_number}
                onChange={(e) => setNewAgent({ ...newAgent, phone_number: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="agent-password">Password</Label>
              <div className="relative">
                <Input
                  id="agent-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Set a secure password"
                  value={newAgent.password}
                  onChange={(e) => setNewAgent({ ...newAgent, password: e.target.value })}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <EyeIcon size={16} />}
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleCreateAgent}
                disabled={creating}
                className="flex-1 bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              >
                {creating ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" /> Creating...</>
                ) : (
                  <><Check size={16} className="mr-2" /> Create Agent</>
                )}
              </Button>
              <Button onClick={handleCancelCreate} variant="outline" className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Agent Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-[420px]">
          <SheetHeader>
            <SheetTitle>Agent Details</SheetTitle>
            <SheetDescription>{currentAgent?.email}</SheetDescription>
          </SheetHeader>

          {currentAgent && (
            <div className="space-y-6 mt-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Full Name:</span>
                  <span className="font-medium">{currentAgent.full_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium">{currentAgent.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium">{currentAgent.phone_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Role:</span>
                  <Badge variant="secondary" className="capitalize">{currentAgent.role || 'agent'}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <Badge variant={currentAgent.status === 'active' ? 'default' : 'destructive'}>
                    {currentAgent.status}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Created:</span>
                  <span className="text-sm">{new Date(currentAgent.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Login:</span>
                  <span className="text-sm">
                    {currentAgent.last_login
                      ? new Date(currentAgent.last_login).toLocaleString()
                      : 'Never'}
                  </span>
                </div>
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant={currentAgent.status === 'active' ? 'destructive' : 'default'}
                    className={currentAgent.status !== 'active' ? 'w-full bg-green-600 hover:bg-green-700' : 'w-full'}
                  >
                    <Ban size={16} className="mr-2" />
                    {currentAgent.status === 'active' ? 'Suspend Agent' : 'Activate Agent'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {currentAgent.status === 'active' ? 'Suspend Agent?' : 'Activate Agent?'}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {currentAgent.status === 'active'
                        ? 'This agent will no longer be able to log in.'
                        : 'This agent will be able to log in again.'}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogAction
                    onClick={() =>
                      handleStatusChange(currentAgent.id, currentAgent.status === 'active' ? 'suspended' : 'active')
                    }
                  >
                    Confirm
                  </AlertDialogAction>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
